import { css } from 'aphrodite';
import { FC, FormHTMLAttributes, ReactNode } from 'react';
import { FieldErrors } from 'react-hook-form';
import { Button } from '../../../../../web/components-v2/Button';
import { bs } from '../../../../../web/styles/styles';
import { styleSheet } from '../../../../components/blogs/styles';
import { InfoPopover } from '../../../../components/InfoPopover/InfoPopover';
import { Column } from '../../../../components/Layout';

interface TemplateFormWrapperProps extends Omit<FormHTMLAttributes<HTMLFormElement>, 'title'> {
	/**
	 * The main title displayed at the top of the form
	 */
	title: string;

	/**
	 * Optional informational message displayed next to the title
	 */
	infoMessage?: string;

	/**
	 * Whether the form is currently submitting
	 */
	isLoading?: boolean;

	/**
	 * Whether the form has been modified
	 */
	isDirty?: boolean;

	/**
	 * Label text for the submit button
	 * @default 'Save & Update'
	 */
	submitLabel?: string;

	/**
	 * Form validation errors
	 */
	errors?: FieldErrors;

	/**
	 * Optional handler for deleting the customization
	 */
	onDeleteCustomization?: () => void;

	/**
	 * Whether the delete button should be disabled
	 */
	isDeleting?: boolean;

	/**
	 * Whether customization exists (controls delete button visibility)
	 */
	hasCustomization?: boolean;

	/**
	 * Form content to render inside the wrapper
	 */
	children: ReactNode;
}

/**
 * Common wrapper component for Template Designer forms.
 *
 * This component provides a consistent structure for all Template Designer forms,
 * including:
 * - Title row with optional info popover
 * - Submit button with loading and error states
 * - Form content area
 *
 * @example
 * ```tsx
 * <TemplateFormWrapper
 *   title="Customize Header"
 *   infoMessage="This form is applicable for both blogs and surveys."
 *   isLoading={mutation.isPending}
 *   errors={formState.errors}
 *   onSubmit={handleSubmit(onValid)}
 * >
 *   <LabeledInput label="Title" {...register('title')} />
 *   <ColorPicker value={color} onChange={setColor} />
 * </TemplateFormWrapper>
 * ```
 */
export const TemplateFormWrapper: FC<TemplateFormWrapperProps> = ({
	title,
	infoMessage,
	isLoading = false,
	isDirty,
	submitLabel = 'Save & Update',
	errors,
	onDeleteCustomization,
	isDeleting = false,
	hasCustomization = false,
	children,
	...formProps
}) => {
	const hasErrors = errors && Object.keys(errors).length > 0;

	return (
		<form {...formProps}>
			<Column className={css(styleSheet.gap24)}>
				<div className={css(bs.flex, bs.flexRow, bs.wFull, bs.itemsCenter, bs.justifyBetween)}>
					<div className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
						<h1 className={css(styleSheet.titleText)}>{title}</h1>
						{infoMessage && <InfoPopover message={infoMessage} />}
					</div>

					<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
						{hasCustomization && onDeleteCustomization && (
							<Button
								disabled={isDeleting || isLoading}
								onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
									e.preventDefault();
									e.stopPropagation();
									onDeleteCustomization();
								}}
								type='button'
								variant='destructive-secondary'
								isLoading={isDeleting}
							>
								Delete Customization
							</Button>
						)}
						<Button disabled={isLoading || hasErrors} type='submit' variant='primary' isLoading={isLoading}>
							{submitLabel}
						</Button>
					</div>
				</div>
				{children}
			</Column>
		</form>
	);
};
